import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { parseAvatarState, type AvatarState, fetchUserAvatarConfig } from "@/lib/avatar";
import { AtomAvatar } from "@/components/AtomAvatar";

export interface MascotDisplayProps {
  mascotId?: string; // Ignored for now - one mascot for all
  state: 'idle' | 'correct' | 'incorrect' | 'celebrating' | 'thinking';
  size: 'sm' | 'md' | 'lg' | number;
}

const SIZE_MAP = {
  sm: 80,
  md: 150,
  lg: 240,
};

// Friendly Carbon atom with Scico blue accent as a fallback mascot
const DEFAULT_MASCOT_STATE: AvatarState = {
  elementN: 6,
  color: '#60A5FA',
  accessory: 'None',
};

const hexToRgbStr = (hex: string) => {
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return '96, 165, 250';
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

export function MascotDisplay({ state, size }: MascotDisplayProps) {
  const [avatarState, setAvatarState] = useState<AvatarState>(DEFAULT_MASCOT_STATE);
  const [visualState, setVisualState] = useState<MascotDisplayProps['state']>('idle');

  const sizePx = typeof size === 'number'
    ? size
    : (!isNaN(Number(size)) && size !== ''
        ? Number(size)
        : (SIZE_MAP[size as keyof typeof SIZE_MAP] || 112));

  // Load the current user's avatar_config using fetchUserAvatarConfig
  useEffect(() => {
    const fetchProfileAvatar = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const config = await fetchUserAvatarConfig(session.user.id);
        setAvatarState(config);
      } catch (err) {
        console.error("Failed to load user avatar config in MascotDisplay:", err);
      }
    };
    fetchProfileAvatar();
  }, []);

  // Temporary visual state updates for correct/incorrect triggers
  useEffect(() => {
    if (state === 'correct') {
      setVisualState('correct');
      const timer = setTimeout(() => {
        setVisualState('idle');
      }, 1000); // fist pump and flash green for 1s, then return to idle
      return () => clearTimeout(timer);
    } else if (state === 'incorrect') {
      setVisualState('incorrect');
      const timer = setTimeout(() => {
        setVisualState('idle');
      }, 800); // droop arms and shake, arms return after 0.8s
      return () => clearTimeout(timer);
    } else {
      setVisualState(state);
    }
  }, [state]);

  const rgbaColor = useMemo(() => {
    return hexToRgbStr(avatarState.color);
  }, [avatarState.color]);

  // Configure transforms, color overrides, opacities, and speeds for each visualState
  const config = useMemo(() => {
    switch (visualState) {
      case 'correct':
        return {
          speedMultiplier: 3.0,
          nucleusColorOverride: '#4ADE80',
          nucleusOpacity: 1.0,
          leftArmTransform: 'rotate(0deg)',
          rightArmTransform: 'rotate(-160deg)',
          leftLegTransform: 'rotate(-10deg)',
          rightLegTransform: 'rotate(10deg)',
          bodyTransform: 'none',
          className: 'mascot-correct',
        };
      case 'incorrect':
        return {
          speedMultiplier: 1.0,
          nucleusColorOverride: '#F87171',
          nucleusOpacity: 1.0,
          leftArmTransform: 'rotate(-30deg)',
          rightArmTransform: 'rotate(30deg)',
          leftLegTransform: 'rotate(-10deg)',
          rightLegTransform: 'rotate(10deg)',
          bodyTransform: 'none',
          className: 'mascot-incorrect',
        };
      case 'celebrating':
        return {
          speedMultiplier: 6.0,
          nucleusColorOverride: '#FFEAA7',
          nucleusOpacity: 1.0,
          leftArmTransform: 'rotate(170deg)',
          rightArmTransform: 'rotate(-170deg)',
          leftLegTransform: 'rotate(-10deg)',
          rightLegTransform: 'rotate(10deg)',
          bodyTransform: 'none',
          className: 'mascot-celebrating',
        };
      case 'thinking':
        return {
          speedMultiplier: 0.25,
          nucleusColorOverride: undefined,
          nucleusOpacity: 0.65,
          leftArmTransform: 'rotate(90deg)',
          rightArmTransform: 'rotate(0deg)',
          leftLegTransform: 'rotate(-10deg)',
          rightLegTransform: 'rotate(10deg)',
          bodyTransform: 'rotate(8deg)',
          className: 'mascot-thinking',
        };
      case 'idle':
      default:
        return {
          speedMultiplier: 0.6,
          nucleusColorOverride: undefined,
          nucleusOpacity: 1.0,
          leftArmTransform: 'rotate(0deg)',
          rightArmTransform: 'rotate(0deg)',
          leftLegTransform: 'rotate(-10deg)',
          rightLegTransform: 'rotate(10deg)',
          bodyTransform: 'none',
          className: 'mascot-idle',
        };
    }
  }, [visualState]);

  return (
    <div
      className={`relative flex items-center justify-center select-none ${config.className}`}
      style={{ width: sizePx, height: sizePx }}
    >
      {/* Injecting CSS keyframes and class overrides locally */}
      <style>{`
        @keyframes idle-pulse {
          0%, 100% { filter: drop-shadow(0 0 12px rgba(${rgbaColor}, 0.35)); }
          50% { filter: drop-shadow(0 0 28px rgba(${rgbaColor}, 0.75)); }
        }
        @keyframes correct-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); filter: drop-shadow(0 0 24px rgba(74, 222, 128, 0.8)); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        @keyframes bounce-scale {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 12px rgba(252, 211, 77, 0.4)); }
          50% { transform: scale(1.12); filter: drop-shadow(0 0 36px rgba(252, 211, 77, 0.9)); }
        }
        @keyframes thinking-pulse {
          0%, 100% { filter: drop-shadow(0 0 6px rgba(${rgbaColor}, 0.25)); }
          50% { filter: drop-shadow(0 0 18px rgba(${rgbaColor}, 0.6)); }
        }
        @keyframes hop {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        .mascot-idle {
          animation: idle-pulse 3s ease-in-out infinite;
        }
        .mascot-correct {
          animation: correct-scale 1s ease-in-out;
        }
        .mascot-incorrect {
          animation: shake 0.4s ease;
          transform: scale(0.95);
        }
        .mascot-celebrating {
          animation: bounce-scale 0.6s ease infinite;
        }
        .mascot-thinking {
          animation: thinking-pulse 4s ease-in-out infinite;
        }
      `}</style>


      {/* Centered Atom Core */}
      <div
        className="pointer-events-none"
        style={{
          width: sizePx,
          height: sizePx,
          transform: config.bodyTransform,
          transition: 'transform 0.4s ease-in-out',
        }}
      >
        <AtomAvatar
          state={visualState === 'correct' ? 'excited' : visualState === 'incorrect' ? 'sad' : visualState === 'idle' ? 'neutral' : visualState}
          size={sizePx}
          speedMultiplier={config.speedMultiplier}
          nucleusColorOverride={config.nucleusColorOverride}
          nucleusOpacity={config.nucleusOpacity}
        />
      </div>
    </div>
  );
}

export default MascotDisplay;
